// The map: levels in a world, and world select
import { GFX } from '../core/graphics';
import { TAG_ICONS, drawGoalIcon } from '../kit';
import { SFX } from '../core/audio';
import { sceneSvg } from '../core/scenes-lib';
import KITCHEN_IMG from '../images/kitchen.jpg';
import { $ } from './dom';
import { show, G, worldOpen, WORLDS, LEVELS, WSTART, payLocked, progOf } from './state';
import { I, setI } from './icons';
import { Store } from '../core/store';
import { t, getLang, onLangChange } from '../core/i18n';
import { loadLevel } from './play';
import { levelThumb } from './render';
import { renderProfiles, headImg, chOf } from './profiles';
import { openLock } from './gate';

export function openMap() {
  const p = Store.prof(); if (!p) { renderProfiles(); show('scrProfiles'); return; }
  show('scrMap');
  const who = $('#whoBtn'); who.innerHTML = headImg(chOf(p), 56);
  // show the world the player has reached (or the one they picked)
  if (G.mapW == null || !worldOpen(G.mapW)) { G.mapW = 0; for (let w = 0; w < WORLDS.length; w++) if (worldOpen(w)) G.mapW = w; }
  const MW = G.mapW;
  $('#worldName').textContent = WORLDS[MW].n;
  const mapBg = MW === 0 ? KITCHEN_IMG : worldThumb(MW);
  if (mapBg) $('#scrMap').style.backgroundImage = `linear-gradient(rgba(255,244,228,.84), rgba(255,230,200,.9)), url("${mapBg}")`;
  const wi = $<HTMLCanvasElement>('#worldIcon'); const wx = wi.getContext('2d')!; wx.setTransform(1, 0, 0, 1, 0, 0); wx.clearRect(0, 0, 108, 108);
  if (MW === 0) { GFX.setWorld('breakfast'); wx.save(); wx.translate(54, 60); wx.scale(1.6, 1.6); drawGoalIcon(wx, GFX); wx.restore(); }
  else { wx.font = '72px serif'; wx.textAlign = 'center'; wx.textBaseline = 'middle'; wx.fillText(WORLDS[MW].ic, 54, 58); }
  const st = Object.values(p.prog).reduce((a, v) => a + v.stars, 0), dk = Object.values(p.prog).filter(v => v.duck).length;
  $('#starTotal').innerHTML = `${I.star}<span>${st}</span>`;
  $('#duckTotal').innerHTML = `<span class="duckico">🦆</span><span>${dk}</span>`;
  // inventor badges
  const bd = $('#badges'); bd.innerHTML = '';
  const learned = new Set<string>(); LEVELS.forEach((L, i) => { if (progOf(p, i)) L.learn.forEach(t => learned.add(t)); });
  for (const tag of ['ramp', 'spring', 'direction', 'lever', 'friction']) {
    const d = document.createElement('div'); d.className = 'badge' + (learned.has(tag) ? '' : ' off');
    const c = document.createElement('canvas'); c.width = 72; c.height = 72; GFX.icon(c, TAG_ICONS[tag]); d.appendChild(c); bd.appendChild(d);
  }
  // levels: cards with a small picture of the level (a taste of what is inside)
  const wrap = $('#mapWrap'); wrap.innerHTML = '';
  const grid = document.createElement('div'); grid.className = 'lgrid'; wrap.appendChild(grid);
  let nextSet = false;
  const i0 = WSTART[MW], i1 = WSTART[MW + 1] != null ? WSTART[MW + 1] : LEVELS.length;
  grid.classList.toggle('many', i1 - i0 > 12);
  LEVELS.forEach((L, i) => {
    if (i < i0 || i >= i1) return;
    const n = document.createElement('button'); n.className = 'lcard';
    const pr = progOf(p, i), open = (Store.d.settings.testAll || i === 0 || !!progOf(p, i - 1));
    n.setAttribute('aria-label', t('aria.level', { n: i - i0 + 1 }));
    n.appendChild(levelThumb(i));
    n.insertAdjacentHTML('beforeend', `<span class="no">${i - i0 + 1}</span>`);
    if (pr) { n.classList.add('done'); n.insertAdjacentHTML('beforeend', `<div class="ns">${[0, 1, 2].map(k => k < pr.stars ? I.star : I.starOff).join('')}</div>`); if (pr.duck) n.insertAdjacentHTML('beforeend', '<span class="dk">🦆</span>'); }
    else if (open && !nextSet && !payLocked(i)) { n.classList.add('next'); nextSet = true; }
    else if (!open || payLocked(i)) n.classList.add('locked');
    if (payLocked(i)) n.insertAdjacentHTML('beforeend', `<span class="lk">${I.lock}</span>`);
    n.addEventListener('click', () => {
      if (payLocked(i)) { SFX.play('nope'); openLock(); return; }
      if (!open) { SFX.play('nope'); n.classList.remove('shake'); void n.offsetWidth; n.classList.add('shake'); return; }
      SFX.play('click'); loadLevel(i);
    });
    grid.appendChild(n);
  });
  // worlds
  const wl = $('#worlds'); wl.innerHTML = '';
  WORLDS.forEach((w, i) => {
    const c = document.createElement('button'); c.className = 'wcard' + (i === MW ? ' on' : ''); c.setAttribute('aria-label', w.n);
    const img = new Image(); img.alt = ''; img.draggable = false; img.src = worldThumb(i); c.appendChild(img);
    c.insertAdjacentHTML('beforeend', `<span class="wic">${w.ic}</span>` + (!worldOpen(i) ? `<span class="lk">${I.lock}</span>` : ''));
    c.addEventListener('click', () => { SFX.play('click'); openWorld(i); });
    wl.appendChild(c);
  });
}
// Scene pictures: a small still on the map, the animated picture when tapped.
// They can have sound words in the current language, so they are cached per language.
const worldThumbs: Partial<Record<string, string[]>> = {};
function worldThumb(i: number) {
  const c = worldThumbs[getLang()] || (worldThumbs[getLang()] = []);
  if (!c[i]) c[i] = i === 0 ? KITCHEN_IMG : 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(sceneSvg(WORLDS[i].k, true));
  return c[i];
}
function renderWorld(i: number) {
  const w = WORLDS[i];
  $('#worldTitle').innerHTML = `<span>${w.ic}</span><span>${w.n}</span>` + (!worldOpen(i) ? I.lock : '');
  $('#worldView').innerHTML = i > 0 ? sceneSvg(w.k) : `<img src="${worldThumb(i)}" alt="">`;
}
function openWorld(i: number) {
  renderWorld(i);
  $('#worldGo').hidden = !worldOpen(i); G.pickW = i;
  $('#ovWorld').hidden = false;
  SFX.track(WORLDS[i].k);
}
// Language changes: redraw an open world (name and sound words). The map behind it is redrawn from parent-panel.ts.
onLangChange(() => { if (!$('#ovWorld').hidden && G.pickW != null) renderWorld(G.pickW); });
function closeWorld() { SFX.track('menu'); $('#ovWorld').hidden = true; $('#worldView').innerHTML = ''; }
setI($('#worldClose'), 'close'); setI($('#worldGo'), 'play');
$('#worldClose').addEventListener('click', () => { SFX.play('click'); closeWorld(); });
$('#worldGo').addEventListener('click', () => { SFX.play('click'); closeWorld(); if (G.pickW != null) { G.mapW = G.pickW; openMap(); } });
$('#whoBtn').addEventListener('click', () => { SFX.play('click'); renderProfiles(); show('scrProfiles'); });
setI($('#homeBtn'), 'map');
$('#homeBtn').addEventListener('click', () => { SFX.play('click'); openMap(); });
